import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import SiteNavbar from '../components/SiteNavbar';
import { supabase } from '../lib/supabaseClient';
import { ensureFrontendSessionId } from '../lib/session';

interface LoginFields {
  email: string;
}

export default function LoginPage() {
  const navigate = useNavigate();

  const [sending, setSending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<LoginFields>();

  // Plus-address warning parity with SignupPage
  const emailVal = watch('email') ?? '';
  const plusAddressing = /^[^@]+\+[^@]+@[^@]+$/.test(emailVal.trim());

  const onSubmit = async (values: LoginFields) => {
    setSending(true);

    const sessionId = await ensureFrontendSessionId();

    const payload = {
      email: values.email.trim().toLowerCase(),
      sessionId,
    } as Record<string, unknown>;

    const { error } = await supabase.functions.invoke('auth_request_otp', {
      body: payload,
    });

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'auth_request_otp',
      level: 'info',
      data: { email: values.email, ok: !error },
    });

    setSending(false);

    if (error) {
      alert(error.message);
      return;
    }

    navigate(`/auth/verify?email=${encodeURIComponent(values.email)}&context=login`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <SiteNavbar />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-lg bg-gray-50 p-8 shadow-lg dark:bg-gray-800">
          <h1 className="mb-4 text-2xl font-bold text-gray-900 dark:text-gray-50">Log in to your account</h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                className={`mt-1 block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('email', { required: 'Required' })}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          {plusAddressing && (
            <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-400">
              Plus-address detected — codes are tied to the primary address (everything before “+” counts as the same inbox).
            </p>
          )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || sending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white shadow hover:bg-primary-dark disabled:opacity-60"
            >
              {sending || isSubmitting ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              {sending || isSubmitting ? 'Sending code…' : 'Send login code'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            Don’t have an account?{' '}
            <a href="/signup" className="font-medium text-primary hover:underline">
              Sign up free
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
